import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EyeIcon } from "@heroicons/react/24/outline";
import { breakSecondsLeft } from "../../utils/eyeBreaks";

export default function EyeBreakOverlay({ attempt, loadedAt, onActiveChange }) {
  const { t } = useTranslation();
  const [secondsLeft, setSecondsLeft] = useState(null);

  const intervalSec = (attempt.eye_break_interval_minutes || 0) * 60;
  const breakSec = attempt.eye_break_seconds || 0;
  const total = (attempt.duration_minutes_snapshot || 0) * 60;
  const remaining = attempt.remaining_seconds;
  const active = secondsLeft != null;

  useEffect(() => {
    if (!intervalSec || !breakSec || !total || remaining == null) return undefined;
    // Anchored on the server's remaining time when the attempt was fetched, so
    // a reload resumes the same break and the fullscreen prompt does not shift it.
    const startedAt = (loadedAt || Date.now()) - (total - remaining) * 1000;
    const tick = () =>
      setSecondsLeft(breakSecondsLeft((Date.now() - startedAt) / 1000, total, intervalSec, breakSec));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [intervalSec, breakSec, total, remaining, loadedAt]);

  useEffect(() => {
    onActiveChange?.(active);
  }, [active, onActiveChange]);

  // A phone that auto-locks while the student looks away would count as leaving.
  useEffect(() => {
    if (!active || !navigator.wakeLock) return undefined;
    let lock = null;
    let ended = false;
    const acquire = () => {
      if (document.visibilityState !== "visible") return;
      navigator.wakeLock
        .request("screen")
        .then((l) => {
          if (ended) l.release();
          else lock = l;
        })
        .catch(() => {});
    };
    acquire();
    document.addEventListener("visibilitychange", acquire);
    return () => {
      ended = true;
      document.removeEventListener("visibilitychange", acquire);
      lock?.release().catch(() => {});
    };
  }, [active]);

  if (!active) return null;

  const left = Math.ceil(secondsLeft);
  const time = `${String(Math.floor(left / 60)).padStart(2, "0")}:${String(left % 60).padStart(2, "0")}`;

  // Below the fullscreen prompt (z-50), so a student who left fullscreen can return.
  return (
    <div
      className="fixed inset-0 flex flex-col items-center justify-center gap-6 px-8 text-center"
      style={{ backgroundColor: "#0f172a", zIndex: 45 }}
    >
      <EyeIcon className="h-16 w-16 text-blue-400" />
      <h2 className="text-white text-2xl font-bold">{t("exam_taking.eye_break_title")}</h2>
      <p className="text-gray-300 max-w-md">{t("exam_taking.eye_break_hint")}</p>
      <p className="font-mono text-5xl font-semibold text-white">{time}</p>
      <p className="text-gray-400 text-sm max-w-md">{t("exam_taking.eye_break_timer_note")}</p>
    </div>
  );
}
